import { useId, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "../lib/cn";

export type SwitchProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "onChange"
> & {
  label: ReactNode;
  description?: ReactNode;
  onCheckedChange?: (checked: boolean) => void;
};

/** Native checkbox semantics styled as a switch, including a full-row target. */
export function Switch({
  id,
  label,
  description,
  className,
  checked,
  defaultChecked,
  disabled,
  onCheckedChange,
  ...props
}: SwitchProps) {
  const generatedId = useId();
  const controlId = id ?? generatedId;

  return (
    <label
      htmlFor={controlId}
      className={cn(
        "flex min-h-11 cursor-pointer items-center justify-between gap-4 rounded-2xl px-1 py-1",
        disabled && "cursor-not-allowed opacity-50",
        className
      )}
    >
      <span className="min-w-0">
        <span className="block text-base font-medium text-foreground">{label}</span>
        {description ? (
          <span className="block text-sm text-foreground-muted">{description}</span>
        ) : null}
      </span>
      <span className="relative inline-flex h-7 w-12 shrink-0">
        <input
          id={controlId}
          type="checkbox"
          role="switch"
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          onChange={(event) => onCheckedChange?.(event.target.checked)}
          className="peer sr-only"
          {...props}
        />
        <span
          aria-hidden
          className="h-7 w-12 rounded-full bg-border transition-colors peer-checked:bg-primary peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-disabled:opacity-50"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute left-1 top-1 h-5 w-5 rounded-full bg-surface-raised shadow-sm transition-transform peer-checked:translate-x-5"
        />
      </span>
    </label>
  );
}
